'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { ElasticVehiclesResponse } from '@//app/api/search/vehicles';
import type { LinkCloudManufacturer } from '@//app/api/search/vehicles/link-cloud';
import { Heading } from '@//components/Heading';
import { LinkCloud } from '@//components/LinkCloud';
import { facetParamsToPath } from '@//utils/vehicles-search/utils';
import styles from './SearchLinkCloud.module.scss';

const MANUFACTURERS_LIMIT = 20;
const MODELS_LIMIT = 20;

interface SearchLinkCloudModelProps {
  searchPage: string;
  linkCloudList: LinkCloudManufacturer[];
  facets: ElasticVehiclesResponse | undefined;
  currentFilters?: {
    manufacturer?: string;
    modelSeries?: string;
    allManufacturers?: string[];
  };
}

const SearchLinkCloudModel = ({
  searchPage,
  linkCloudList,
  facets,
  currentFilters,
}: SearchLinkCloudModelProps) => {
  const tCommon = useTranslations('other.common');
  const tSearch = useTranslations('pages.search');

  const enrichedManufacturers = useMemo(() => {
    const manufacturerFacetLookup: Record<string, number> | undefined = facets?.facets
      .find(({ id }) => id === 'manufacturer')
      ?.filters.reduce(
        (acc, { displayName, count }) => {
          acc[displayName] = count;
          return acc;
        },
        {} as Record<string, number>,
      );

    return linkCloudList.map((m) => ({
      ...m,
      count: manufacturerFacetLookup?.[m.displayName] ?? m.count,
    }));
  }, [linkCloudList, facets]);

  const topManufacturers = useMemo(
    () => enrichedManufacturers.slice(0, MANUFACTURERS_LIMIT),
    [enrichedManufacturers],
  );

  const selectedManufacturerData = useMemo(() => {
    // Only show specific models if exactly one manufacturer is selected and no model is selected
    const manufacturerCount = currentFilters?.allManufacturers?.length ?? 0;

    // Return null if:
    // - No manufacturers selected
    // - More than one manufacturer selected
    // - A model series is already selected
    if (manufacturerCount !== 1 || currentFilters?.modelSeries) {
      return null;
    }

    const matchingManufacturer = linkCloudList.find(
      (m) => m.displayName === currentFilters?.manufacturer,
    );

    return matchingManufacturer;
  }, [currentFilters, linkCloudList]);

  const modelSeriesFacets = useMemo(() => {
    if (!selectedManufacturerData) {
      return null;
    }

    // Always use the pre-fetched model series from linkCloudList
    // as they are already filtered for this specific manufacturer
    return selectedManufacturerData.modelSeries.map((ms) => ({
      displayName: ms.displayName,
      count: ms.count,
      query: ms.query,
      selected: false,
    }));
  }, [selectedManufacturerData]);

  const cloudLinksModels = useMemo(() => {
    if (selectedManufacturerData && modelSeriesFacets && modelSeriesFacets.length > 0) {
      const topModels = modelSeriesFacets.slice(0, MODELS_LIMIT);

      return topModels.map((model) => {
        const params = new URLSearchParams(model.query);
        params.delete('hits');
        const slug = facetParamsToPath(params, facets?.facets);

        const href = slug ? `${searchPage}/${slug}` : `${searchPage}?${model.query}`;

        return {
          href,
          label: `${selectedManufacturerData.displayName} ${model.displayName}`,
          title: tCommon('seoLinks.query', {
            query: `${selectedManufacturerData.displayName} ${model.displayName}`,
          }),
          ariaLabel: tCommon('seoLinks.query', {
            query: `${selectedManufacturerData.displayName} ${model.displayName}`,
          }),
        };
      });
    }

    return topManufacturers.map((m) => {
      const params = new URLSearchParams(m.query);
      params.delete('hits');
      const slug = facetParamsToPath(params, facets?.facets);

      const href = slug ? `${searchPage}/${slug}` : `${searchPage}?${m.query}`;

      return {
        href,
        label: m.displayName,
        title: tCommon('seoLinks.query', {
          query: `${m.displayName}`,
        }),
        ariaLabel: tCommon('seoLinks.query', {
          query: `${m.displayName}`,
        }),
      };
    });
  }, [
    selectedManufacturerData,
    modelSeriesFacets,
    topManufacturers,
    searchPage,
    facets?.facets,
    tCommon,
  ]);

  if (cloudLinksModels.length === 0) {
    return null;
  }

  const headingId = 'search-link-cloud-heading-model';

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <Heading level={2} size="xl" id={headingId}>
        {selectedManufacturerData
          ? tSearch('linkCloud.findNewAndUsedManufacturer', {
              manufacturer: selectedManufacturerData.displayName,
            })
          : tSearch('linkCloud.findNewAndUsedCars')}
      </Heading>
      <LinkCloud links={cloudLinksModels} testId="search-link-cloud-model" />
    </section>
  );
};

export default SearchLinkCloudModel;
